import './Avatar.css'

import { clsx } from 'clsx'
import { type ReactElement, useState } from 'react'

export type AvatarSize = 'sm' | 'md' | 'lg'

export interface AvatarProps {
  /** Drives the initials and the deterministic 1-of-8 color slot; also the image's alt text. */
  name: string
  /** Profile image. Takes precedence over initials; falls back to initials if missing or it errors. */
  src?: string | undefined
  /** Who the avatar represents — person renders round (default), entity (team/org/etc.) renders square. */
  type?: 'person' | 'entity' | undefined
  /** Fixed size step. Defaults to medium. */
  size?: AvatarSize | undefined
  className?: string
}

const SLOT_COUNT = 8

/** First letter of the first and last word (or the first two letters of a single word). */
const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0] ?? ''
  if (parts.length <= 1) {
    return first.slice(0, 2).toUpperCase() || '?'
  }
  const last = parts[parts.length - 1] ?? ''
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
}

/** Deterministic 1-of-8 color slot from the name (same name → same slot). */
const slotFromName = (name: string): number => {
  let hash = 0
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) | 0
  }
  return (Math.abs(hash) % SLOT_COUNT) + 1
}

/**
 * Avatar shows a person's or entity's identity: a profile image when available, otherwise
 * color-keyed initials derived from `name`. With an image, the initials are hidden and the fill is the
 * card color, so transparent logos don't show them through. They reappear if the image fails to load.
 */
export const Avatar = ({
  name,
  src,
  type = 'person',
  size = 'md',
  className,
}: AvatarProps): ReactElement => {
  const [hasImageErrored, setHasImageErrored] = useState(false)
  const showImage = src !== undefined && !hasImageErrored
  return (
    <span
      className={clsx('doss-avatar', type, size, className)}
      data-avatar-slot={slotFromName(name)}
    >
      <span aria-hidden={showImage} className="initials">
        {initialsFromName(name)}
      </span>
      {showImage ? (
        <img
          alt={name}
          className="image"
          onError={() => {
            setHasImageErrored(true)
          }}
          src={src}
        />
      ) : null}
    </span>
  )
}
